/**
 * A card pinned to the cursor over the map.
 *
 * Both overlays need one, and both need the same edge behaviour: near the right
 * of the map the card flips to the left of the pointer rather than sliding under
 * the sidebar, where the legacy district card used to disappear exactly over the
 * districts a reader coming from the ranking list would look at first.
 *
 * Positioned in the map container's own coordinates, so it does not care where
 * the map sits on the page.
 */

import type { ReactNode } from "react";

export interface Anchor {
  /** Cursor position relative to the map container. */
  x: number;
  y: number;
  /** The container's width, which decides whether the card has to flip. */
  containerWidth: number;
}

interface Props {
  anchor: Anchor | null;
  /** Widest the card is allowed to get; also what the flip is measured against. */
  width: number;
  className?: string;
  children: ReactNode;
}

const GAP = 14;

export function HoverCard({ anchor, width, className, children }: Props) {
  if (!anchor) return null;

  const right = anchor.x + GAP;
  const flip = right + width > anchor.containerWidth;
  const left = flip ? anchor.x - width - GAP : right;

  return (
    <div
      className={className ? `hover-card ${className}` : "hover-card"}
      style={{
        // Never above the top edge: a card anchored to a segment near the top of
        // the viewport would otherwise be clipped by the bar above the map.
        top: Math.max(4, anchor.y - 10),
        left: Math.max(4, left),
        width,
      }}
    >
      {children}
    </div>
  );
}
